<template>
  <div class="comps-cards">
    <!-- Toolbar -->
    <table-toolbar
      v-if="showToolbar"
      :show-title="showTitle"
      :title="title"
      :show-count="showDisplayCount"
      :current-count="filteredComps.length"
      :total-count="compData.length"
      :searchable="showSearch"
      :search-query="searchQuery"
      :show-view-toggle="showViewToggle"
      :view-mode="viewMode"
      :show-sort-filter="showSortFilter"
      :sort-options="sortOptions"
      :sort-by="sortColumn"
      :sort-direction="sortDirection"
      :filter-options="filterOptions"
      :show-refresh="false"
      @search-change="searchQuery = $event"
      @view-mode-change="viewMode = $event"
      @sort-change="sortColumn = $event"
      @sort-direction-change="sortDirection = $event"
      @filter-change="handleFilterChange"
      @clear-filters="handleClearFilters"
    >
      <template #action-buttons>
        <!-- Manage Comps Button -->
        <themed-button
          v-if="showManageButton"
          variant="default"
          size="default"
          @click="$emit('manage-comps')"
          aria-label="Manage comps"
        >
          <theme-icon icon="plus-icon" type="icons" classes="w-4 h-4" />
          <span>Manage Comps</span>
        </themed-button>
      </template>
    </table-toolbar>

    <!-- Table View -->
    <div v-if="hasComps && viewMode === 'list'" class="comps-cards__table-container">
      <themed-table
        :columns="tableColumns"
        :data="tableData"
        :hoverable="true"
        :sortable="true"
        aria-label="Comps Table"
        @row-click="handleRowClick"
        @action="handleTableAction"
        :show-row-details="true"
        row-details-title="Comps Details"
      >
      </themed-table>
    </div>

    <!-- Grid View -->
    <themed-grid
      v-if="hasComps && viewMode === 'grid'"
      :data="tableData"
      :fields="gridFields"
      :columns="3"
      :pagination="false"
      :clickable="true"
      @card-click="viewComp"
    >
      <template #actions="{ item }">
        <themed-button
          variant="ghost"
          size="sm"
          @click.stop="toggleFavorite(item)"
          :class="{ 'comps-cards__action-button--active': item.is_favorite }"
          :aria-label="item.is_favorite ? 'Remove from favorites' : 'Add to favorites'"
        >
          <theme-icon
            :icon="item.is_favorite ? 'heart-filled' : 'heart-outline'"
            type="icons"
            classes="w-4 h-4"
          />
        </themed-button>

        <themed-button
          v-if="showRemoveButton"
          variant="ghost"
          size="sm"
          @click.stop="removeComp(item)"
          aria-label="Remove comp"
        >
          <theme-icon icon="x" type="icons" classes="w-4 h-4" />
        </themed-button>
      </template>
    </themed-grid>
  </div>
</template>

<script>
import moment from 'moment';
import ThemedButton from '../ThemedButton.vue';
import ThemedTable from '../ThemedTable.vue';
import ThemedGrid from '../ThemedGrid.vue';
import ThemeIcon from '../ThemeIcon.vue';
import TableToolbar from '../table/TableToolbar.vue';

export default {
  name: 'CompsCards',

  components: {
    ThemedButton,
    ThemedTable,
    ThemedGrid,
    ThemeIcon,
    TableToolbar
  },

  props: {
    compData: {
      type: Array,
      required: true,
      default: () => []
    },
    projectId: {
      type: Number,
      required: false
    },
    showToolbar: {
      type: Boolean,
      default: true
    },
    showTitle: {
      type: Boolean,
      default: true
    },
    showSearch: {
      type: Boolean,
      default: true
    },
    showViewToggle: {
      type: Boolean,
      default: true
    },
    showSortFilter: {
      type: Boolean,
      default: true
    },
    showDisplayCount: {
      type: Boolean,
      default: true
    },
    showManageButton: {
      type: Boolean,
      default: true
    },
    showRemoveButton: {
      type: Boolean,
      default: true
    },
    title: {
      type: String,
      default: 'Comps'
    },
    emptyMessage: {
      type: String,
      default: 'No comps added yet'
    },
    defaultViewMode: {
      type: String,
      default: 'list',
      validator: (value) => ['grid', 'list'].includes(value)
    },
    defaultSortBy: {
      type: String,
      default: ''
    },
    defaultSortDirection: {
      type: String,
      default: 'desc',
      validator: (value) => ['asc', 'desc'].includes(value)
    },
    disablePreferences: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      viewMode: this.defaultViewMode,
      sortColumn: this.defaultSortBy,
      sortDirection: this.defaultSortDirection,
      searchQuery: '',
      showFavorited: false,
      showForSale: false
    };
  },

  computed: {
    sortOptions() {
      return [
        { label: 'None', value: '' },
        { label: 'Favorited', value: 'favorited' },
        { label: 'Address', value: 'address' },
        { label: 'Available Sq Ft', value: 'avail_sq_ft' },
        { label: 'Sq Ft', value: 'sq_ft' },
        { label: 'Score', value: 'score' }
      ];
    },

    filterOptions() {
      return [
        {
          type: 'toggle',
          label: 'Show Favorited Only',
          value: this.showFavorited,
          model: 'showFavorited'
        },
        {
          type: 'toggle',
          label: 'Show For Sale Only',
          value: this.showForSale,
          model: 'showForSale'
        }
      ];
    },
    tableColumns() {
      return [
        { 
          field: 'image', 
          label: '', 
          sortable: false,
          type: 'image',
          imageClass: 'comps-cards__image'
        },
        { 
          field: 'address', 
          label: 'Address', 
          sortable: true, 
          align: 'left',
          formatter: (value, row) => {
            return `<a href="${row.view_url}" class="comps-cards__address-link">
                      <div class="comps-cards__address-line1">${row.address.line1}</div>
                      <div class="comps-cards__address-line2">${row.address.city}, ${row.address.state}</div>
                    </a>`;
          }
        },
        { 
          field: 'score', 
          label: 'Score', 
          showInTable: false, 
          sortable: true, 
          align: 'right', 
          width: '5%',
          formatter: (value, row) => {
            return `<div class="comps-cards__score-badge comps-cards__score-badge--${row.score_color}">${value}</div>`;
          }
        },
        { field: 'type', label: 'Type', showInTable: false, sortable: true },
        { field: 'floors', label: 'Floors', showInTable: false, sortable: true, align: 'right', width: '5%' },
        { 
          field: 'sq_ft', 
          label: 'Sq. Ft.', 
          sortable: true, 
          align: 'right',
          formatter: (value) => this.formatCompactNumber(value)
        },
        { 
          field: 'avail_sq_ft', 
          width: '20%', 
          label: 'Avail Sq. Ft.', 
          sortable: true, 
          align: 'right',
          formatter: (value) => this.formatCompactNumber(value)
        },
        { field: 'units', label: '# Units', showInTable: false, sortable: true, align: 'right', width: '100px' },
        { 
          field: 'ask_price',
          label: 'Ask Price',
          showInTable: false,
          sortable: true,
          align: 'right',
          // ask_price is a PSF figure stored in cents. formatCompactNumber does no
          // currency conversion; formatCurrency (global mixin) divides by 100, which
          // is what Units.vue and CompPopup.vue already use for this same field.
          formatter: (value) => this.formatCurrency(value, 2)
        },
        { 
          field: 'actions', 
          label: '', 
          sortable: false, 
          width: '120px', 
          align: 'center',
          type: 'actions',
          buttons: [
            {
              name: 'favorite',
              label: (row) => row.is_favorite ? 'Remove from favorites' : 'Add to favorites',
              icon: (row) => row.is_favorite ? 'heart-filled' : 'heart-outline',
              variant: 'ghost',
              size: 'sm',
              iconOnly: true,
              class: (row) => row.is_favorite ? 'comps-cards__action-button--active' : '',
              visible: () => true
            },
            {
              name: 'remove',
              label: 'Remove comp from project',
              icon: 'x',
              variant: 'ghost',
              size: 'sm',
              iconOnly: true,
              class: 'comps-cards__action-button--remove',
              visible: () => this.showRemoveButton
            }
          ]
        }
      ];
    },

    filteredComps() {
      let comps = [...this.compData];
      
      // Apply search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        comps = comps.filter(comp => 
          comp.address_line_1?.toLowerCase().includes(query) ||
          comp.city?.toLowerCase().includes(query) ||
          comp.state?.toLowerCase().includes(query) ||
          comp.type?.type?.toLowerCase().includes(query)
        );
      }
      
      // Apply favorited filter
      if (this.showFavorited) {
        comps = comps.filter(comp => comp.pivot?.is_favorite);
      }
      
      // Apply for sale filter
      if (this.showForSale) {
        comps = comps.filter(comp => comp.for_sale);
      }
      
      // Apply sorting
      if (this.sortColumn) {
        comps = [...comps].sort((a, b) => {
          let aVal, bVal;
          
          switch (this.sortColumn) {
            case 'score':
              aVal = a.pivot?.score || 0;
              bVal = b.pivot?.score || 0;
              break;
            case 'address':
              aVal = a.address_line_1 || '';
              bVal = b.address_line_1 || '';
              break;
            case 'sq_ft':
              aVal = a.total_square_footage || 0;
              bVal = b.total_square_footage || 0;
              break;
            case 'avail_sq_ft':
              aVal = a.available_square_footage || 0;
              bVal = b.available_square_footage || 0;
              break;
            case 'ask_price':
              aVal = a.pivot?.ask_price || 0;
              bVal = b.pivot?.ask_price || 0;
              break;
            case 'favorited':
              aVal = a.pivot?.is_favorite ? 1 : 0;
              bVal = b.pivot?.is_favorite ? 1 : 0;
              break;
            default:
              return 0;
          }
          
          if (typeof aVal === 'string') {
            return this.sortDirection === 'asc' 
              ? aVal.localeCompare(bVal)
              : bVal.localeCompare(aVal);
          }
          
          return this.sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
        });
      }
      
      return comps;
    },

    tableData() {
      return this.filteredComps.map(comp => ({
        id: comp.id,
        pivot_id: comp.pivot?.id,
        image: comp.image?.view_url || '',
        address: {
          line1: comp.address_line_1,
          city: comp.city,
          state: comp.state
        },
        score: comp.pivot?.score || 0,
        score_color: comp.pivot?.score_color || '',
        type: comp.type?.type || 'N/A',
        floors: comp.floors || 0,
        sq_ft: comp.total_square_footage || 0,
        avail_sq_ft: comp.available_square_footage || 0,
        units: comp.units?.length || 0,
        ask_price: comp.pivot?.ask_price || 0,
        view_url: comp.pivot?.view_url || `/sites/${comp.id}`,
        is_favorite: comp.pivot?.is_favorite || false
      }));
    },

    hasComps() {
      return this.tableData.length > 0;
    },

    gridFields() {
      return [
        {
          field: 'image',
          type: 'image',
          defaultImage: '/img/placeholder-building.jpg'
        },
        {
          field: 'score',
          type: 'badge',
          position: 'topRight',
          badge: true,
          classFunction: (item) => `comps-cards__card-score-badge comps-cards__card-score-badge--${item.score_color}`
        },
        {
          field: 'address.line1',
          type: 'title'
        },
        {
          field: 'address',
          type: 'subtitle',
          transform: (value) => `${value.city}, ${value.state}`
        },
        {
          field: 'type',
          label: 'TYPE:',
          type: 'text',
          labelFirst: true
        },
        {
          field: 'sq_ft',
          label: 'SQ. FT.:',
          type: 'text',
          labelFirst: true,
          transform: (value) => this.formatCompactNumber(value)
        },
        {
          field: 'avail_sq_ft',
          label: 'AVAIL SQ. FT.:',
          type: 'text',
          labelFirst: true,
          transform: (value) => this.formatCompactNumber(value)
        },
        {
          field: 'ask_price',
          label: 'ASK PRICE:',
          type: 'text',
          labelFirst: true,
          transform: (value) => this.formatCurrency(value, 2)
        }
      ];
    }
  },

  mounted() {
    // Load saved view mode preference
    if (!this.disablePreferences) {
      const savedViewMode = localStorage.getItem('comps_view_mode');
      if (savedViewMode) {
        this.viewMode = savedViewMode;
      }
    }
  },

  watch: {
    viewMode(newValue) {
      // Save preference to localStorage if enabled
      if (!this.disablePreferences) {
        localStorage.setItem('comps_view_mode', newValue);
      }
    }
  },

  methods: {
    handleFilterChange(event) {
      // Handle filter changes from the toolbar
      if (event.filter === 'showFavorited') {
        this.showFavorited = event.value;
      } else if (event.filter === 'showForSale') {
        this.showForSale = event.value;
      }
    },

    handleClearFilters() {
      // Reset filters and sorting to empty/none
      this.sortColumn = '';
      this.sortDirection = 'asc';
      this.showFavorited = false;
      this.showForSale = false;
    },

    handleTableAction(payload) {
      // Handle action button clicks from both table and grid
      const action = payload.action || payload;
      const row = payload.row || payload.item;
      
      if (action === 'favorite') {
        this.toggleFavorite(row);
      } else if (action === 'remove') {
        this.removeComp(row);
      }
    },

    formatCompactNumber(value) {
      if (!value && value !== 0) return 'N/A';
      if (value >= 1000000) {
        return (value / 1000000).toFixed(1) + 'M';
      }
      if (value >= 1000) {
        return (value / 1000).toFixed(1) + 'K';
      }
      return value.toString();
    },

    handleRowClick({ row }) {
      if (row && row.view_url) {
        window.location.href = row.view_url;
      }
    },

    viewComp(comp) {
      window.location.href = comp.view_url;
    },

    async toggleFavorite(comp) {
      const previousState = comp.is_favorite;
      const favorited_at = previousState ? null : moment().format('Y-MM-D H:m:ss');
      const favorited_by = previousState ? null : window.user.id;
      
      // Optimistic update
      comp.is_favorite = !previousState;
      
      try {
        if (this.projectId) {
          const compId = comp.pivot_id || comp.id;
          await axios.patch(`/projects/${this.projectId}/comps/${compId}`, {
            favorited_at: favorited_at,
            favorited_by: favorited_by
          });
        }
        
        this.$emit('favorite-toggled', { comp, is_favorite: comp.is_favorite });
        
        this.$notify({
          type: 'success',
          title: 'Success',
          text: comp.is_favorite ? 'Added to favorites' : 'Removed from favorites'
        });
      } catch (err) {
        // Revert on error
        comp.is_favorite = previousState;
        console.error('Error toggling favorite:', err);
        this.$notify({
          type: 'error',
          title: 'Error',
          text: 'Unable to update favorite status'
        });
      }
    },

    removeComp(comp) {
      this.$swal({
        title: 'Remove Comp?',
        text: `Are you sure you want to remove ${comp.address.line1} from this project?`,
        type: 'warning',
        buttonsStyling: false,
        confirmButtonText: 'Yes - Remove it',
        confirmButtonClass: 'btn btn-red',
        showCancelButton: true,
        cancelButtonClass: 'btn btn-secondary mr-4',
        reverseButtons: true
      }).then(async (result) => {
        if (result.value) {
          try {
            if (this.projectId) {
              const compId = comp.pivot_id || comp.id;
              await axios.delete(`/projects/${this.projectId}/comps/${compId}`);
            }
            
            this.$emit('comp-removed', comp);
            
            this.$notify({
              type: 'success',
              title: 'Success',
              text: 'Comp removed from project'
            });
          } catch (err) {
            console.error('Error removing comp:', err);
            this.$notify({
              type: 'error',
              title: 'Error',
              text: 'Unable to remove comp'
            });
          }
        }
      });
    }
  }
};
</script>
